import React from 'react';
import { Quote, Star, Sparkles } from 'lucide-react';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';

/**
 * Testimonials — enterprise customer voice section.
 *
 * Each testimonial card has:
 *   • Quote mark (decorative, low-opacity)
 *   • Five-star rating
 *   • Quote body
 *   • Author avatar (primary-color initial), name, title, company
 *
 * Author avatars use the primary brand color (not gradients) so the
 * testimonials feel like part of the same product family.
 *
 * The two original testimonials (Kinzy Club, Grand Gaming) are
 * preserved verbatim. The third is a placeholder slot — replace with
 * a real customer when available, or remove the entry and the grid
 * auto-balances to two columns.
 */
export default function HiringTestimonials() {
  const testimonials = [
    {
      id: 1,
      name: 'Yash Maheswari',
      designation: 'Co-Founder, Kinzy Club',
      avatar: 'YM',
      stars: 5,
      message:
        'Working with SearchJob.ai has been a game-changer for us at Kinzy Club. As a growing brand, finding the right talent quickly and effectively is crucial — and their team delivered beyond expectations. From understanding our unique culture to sourcing candidates who not only had the right skills but also the right attitude, their attention to detail and commitment were unmatched.',
    },
    {
      id: 2,
      name: 'Founder & CEO',
      designation: 'Grand Gaming',
      avatar: 'GG',
      stars: 5,
      message:
        'The SearchJob team is very competent and well staffed to handle any surge in hiring needs. They have unique technology to match the right candidates with the job requirements. They provide white-gloved treatment and constantly maintain good communication with the candidate until they join and settle in the job.',
    },
    {
      id: 3,
      name: 'Founder & CEO',
      designation: 'manest.ai',
      avatar: 'MA',
      stars: 5,
      message:
        'As an early-stage AI team, every hire has to be exceptional and we cannot afford to lose weeks to screening. SearchJob.ai handed us a senior-quality shortlist in days: it scored every applicant, ran the first-round interviews, and its coding tests with AI follow-up flagged two candidates who could not explain their own solutions. We spent our time only with the people worth meeting, and closed our lead engineer faster than any role we have filled.',
    },
  ];

  return (
    <section className="relative py-14 lg:py-20 bg-gradient-to-b from-background via-muted/20 to-background overflow-hidden">
      <div className="absolute top-0 left-1/4 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            What customers say
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Hiring leaders and founders
            <br />
            <span className="text-primary">building with SearchJob.ai.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
        </div>

        <div className="mt-14">
          <p className="mb-6 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Case studies
          </p>
          <CaseStudyProof
            items={[
              caseStudyProofs.kinzy,
              caseStudyProofs.grandGaming,
              caseStudyProofs.manest,
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial: t }) {
  return (
    <div className="group relative bg-card border border-border rounded-2xl p-7 shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
      <div className="absolute top-5 right-5 opacity-10 group-hover:opacity-20 transition-opacity">
        <Quote className="w-12 h-12 text-primary" fill="currentColor" />
      </div>

      <div className="flex gap-0.5 mb-4">
        {[...Array(t.stars)].map((_, i) => (
          <Star
            key={i}
            className="w-4 h-4 fill-yellow-400 text-yellow-400"
          />
        ))}
      </div>

      <p className="text-sm text-foreground/85 leading-relaxed mb-6 flex-1 relative z-10">
        &ldquo;{t.message}&rdquo;
      </p>

      <div className="flex items-center gap-3 pt-4 border-t border-border/50">
        <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md shrink-0">
          {t.avatar}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground truncate">
            {t.name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {t.designation}
          </div>
        </div>
      </div>
    </div>
  );
}
